import React, { useEffect, useRef } from 'react'
import { Drawer } from '@pushly/aqe/lib/components'
import { useService } from '../../hooks/use-service'
import { AppState } from '../../stores/app'
import { NotificationDto } from '../../features/notifications'
import { useLoadableDataState } from '../../hooks/use-loadable-data-state'
import { NotificationBuilderRef } from '../notification-builder/notification-builder'
import { buildSubmittedNotificationPackages } from '../notification-builder/helpers'
import { AppService } from '../../services'
import { useRefEffect } from '../../hooks/use-ref-effect'
import { getCustomMacroFields, getDomainKeywords } from '../../_utils/domain'
import { INotificationBuilderState } from '../notification-builder/interfaces/notification-builder-state.interface'
import { DomainDto } from '../../dtos/domain'
import { getClassNames } from '../../_utils/classnames'
import { DomainNotificationBuilderView } from '../notification-builder/views/domain-notification-builder-view'

interface IProps {
    className?: string
    visible: boolean
    defaultValue?: any
    onSubmit: (notification: any, builder: INotificationBuilderState) => any
    onClose: () => void
}

const parseInitialNotificationFromTemplate = (template: NotificationDto | any, domain: DomainDto) => {
    let res = template

    // backfill default abandoned cart details
    if (res && !res.deliverySpec) {
        res.deliverySpec = {
            isSilent: false,
            requireInteraction: true,
            ttlSeconds: domain.defaultNotificationTtlSeconds,
            ttlMetric: 'days',
        }
    }

    return res
}

export const FallbackNotificationEditor = (props: IProps) => {
    const appState = useService<AppState>(AppState)
    const appService = useService<AppService>(AppService)

    const domain = appState.currentDomain!
    const { className, visible, onSubmit, onClose, defaultValue } = props

    const [notifState, setNotifState] = useLoadableDataState<NotificationDto | undefined>({ loading: true })
    const [keywordOptions] = useRefEffect(() => getDomainKeywords(domain.id), [domain.id])
    const [customMacroOptions] = useRefEffect(() => getCustomMacroFields(domain.id), [domain.id])

    // references required for outer drawer submit
    const builderRef = useRef<NotificationBuilderRef>(null)

    // run once on mount
    useEffect(() => {
        let notification

        if (defaultValue) {
            notification = parseInitialNotificationFromTemplate(defaultValue, domain)
        }

        setNotifState({
            loading: false,
            data: notification,
        })
    }, [!!defaultValue])

    return (
        <Drawer
            getContainer={appService.getAppContainer}
            className={getClassNames('ddf-fallback-notif-editor')}
            title="Update Fallback Notification"
            placement="right"
            closable={true}
            onSubmit={async () => {
                if (!builderRef.current) return

                const builder = builderRef.current.state
                const validateSubmit = builderRef.current.validate

                const valid = await validateSubmit(builder, domain, appState.flags, true)
                if (valid) {
                    const build = buildSubmittedNotificationPackages(domain, builder, appState.flags)[0]

                    // pass back action updates to action.editor.tsx
                    await onSubmit(build, builder)
                }
            }}
            onClose={onClose}
            open={visible}
        >
            {notifState.loading ? null : (
                <DomainNotificationBuilderView
                    ref={builderRef}
                    formMode="create"
                    isInDrawer={true}
                    hidePageHeader={true}
                    hidePageAnalysisToggle={true}
                    hidePushlyAiToggle={true}
                    hideAudienceSelection={true}
                    hideDeliveryChannels={true}
                    hideScheduling={true}
                    hideTemplateSelector={true}
                    hideVariantManager={true}
                    hideEstimatedReach={true}
                    hideSubmit={true}
                    hideTestConfig={true}
                    builderProps={{
                        className,
                        notifications: [notifState.data!],
                        keywordOptions,
                        customMacroOptions,
                    }}
                />
            )}
        </Drawer>
    )
}
